import React, { useState, CSSProperties } from 'react';
import {
  Button,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  IconButton,
  DialogTitle,
  Grid,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from 'moment';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import {
  useGetUserNotesQuery,
  useAddNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
} from '../services/users.endpoints';
import { Note } from '../schemas/userDetails.schema';

const styles = {
  addButton: {
    margin: '20px 0',
  },
  listItem: {
    borderRadius: '8px',
    boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
    '&:not(:last-child)': {
      marginBottom: '10px',
    },
    background: 'white',
    border: '1px solid rgba(0,0,0,0.12)',
  },
  editIcon: {
    color: '#4CAF50',
  },
  deleteIcon: {
    color: '#F44336',
  },
  noteContent: {
    fontSize: '1rem',
  },
};

const emptyNotesStyle: CSSProperties = {
  border: '2px dotted #000',
  borderRadius: '8px',
  padding: '16px',
  textAlign: 'center',
  color: '#000',
  margin: '20px 0',
};

type NoteModalState = {
  _id: string;
  content: string;
  hidden?: boolean;
};

const NotesComponent = ({
  userId,
  isAdmin,
}: {
  userId: string;
  isAdmin: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [currentNote, setCurrentNote] = useState<NoteModalState>({
    _id: '',
    content: '',
    hidden: false,
  });
  const [editMode, setEditMode] = useState(false);
  const {
    data: notes,
    refetch,
    error: notesError,
  } = useGetUserNotesQuery(userId);
  const [addNote, { error: addError }] = useAddNoteMutation();
  const [updateNote] = useUpdateNoteMutation();
  const [deleteNote] = useDeleteNoteMutation();

  const handleOpen = (note?: Note) => {
    if (note) {
      setEditMode(true);
      setCurrentNote({
        _id: note._id,
        content: note.content,
        hidden: note.hidden,
      });
    } else {
      setEditMode(false);
      setCurrentNote({ _id: '', content: '', hidden: false });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async () => {
    const { _id: noteId, content, hidden } = currentNote;
    if (editMode) {
      await updateNote({
        userId,
        noteId,
        content,
        hidden,
      });
    } else {
      await addNote({
        userId,
        content,
        hidden,
      });
    }
    setOpen(false);
    refetch();
  };

  const handleDelete = async (noteId: string) => {
    await deleteNote({
      userId,
      noteId,
    });
    refetch();
  };

  return (
    <Grid item xs={12}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ mr: 2, fontWeight: 'bold' }}>
          Support and Interactions
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              handleOpen();
            }}
          >
            Add Note
          </Button>
        )}
      </div>
      {notesError && (
        <Alert severity="error">{formatQueryError(notesError)}</Alert>
      )}
      <List>
        {notes && notes.length > 0 ? (
          <List>
            {notes.map((note: Note) => (
              <ListItem
                key={note._id}
                sx={styles.listItem}
                secondaryAction={
                  isAdmin ? (
                    <>
                      <Tooltip
                        title={`This note is ${
                          note.hidden ? 'hidden from' : 'visible to'
                        } the user.`}
                      >
                        <IconButton
                          onClick={() => {
                            updateNote({
                              noteId: note._id,
                              userId,
                              hidden: !note.hidden,
                            });
                          }}
                        >
                          {note.hidden ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        sx={styles.editIcon}
                        edge="end"
                        aria-label="edit"
                        onClick={() => handleOpen(note)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        sx={styles.deleteIcon}
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDelete(note._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  ) : (
                    ''
                  )
                }
              >
                <ListItemText
                  primary={note.content}
                  primaryTypographyProps={{ sx: styles.noteContent }}
                  secondary={`${moment(note.createdAt).format(
                    'MMM DD, YYYY',
                  )} at ${moment(note.createdAt).format('hh:mm A')}`}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <div style={emptyNotesStyle}>No notes</div>
        )}
      </List>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editMode ? 'Edit Note' : 'Add Note'}</DialogTitle>
        <DialogContent>
          {addError && (
            <Alert severity="error">{formatQueryError(addError)}</Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            id="content"
            label="Note Content"
            type="text"
            fullWidth
            variant="outlined"
            value={currentNote.content}
            onChange={(e) =>
              setCurrentNote({ ...currentNote, content: e.target.value })
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={currentNote.hidden}
                onChange={(e) =>
                  setCurrentNote({ ...currentNote, hidden: e.target.checked })
                }
              />
            }
            label="Hide from user"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit}>{editMode ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default NotesComponent;
